<!-- Copyright: Seán I. O'Donoghue -->
<!-- The standard segmented control (2026-10-26): the fullstack's App/SegmentedControl.vue -->
<!-- (version 10, the look of this site's YearSidebar/SidebarTabs.vue), for the Frontend: a -->
<!-- rounded track, the chosen option's capsule sliding to it. v-model: the chosen option. -->
<template>
  <!-- keyboard (the tabs pattern): one Tab stop, the selected option; the arrow keys, Space and
       Enter choose the next; the focus ring goes round the whole control -->
  <div
    ref="toggleRef"
    class="toggle"
    role="tablist"
    :aria-label="label"
    @keydown="onKeydown">
    <!-- the chosen one's fill, behind the options, sliding from one to the next -->
    <span class="fill" :style="fillStyle" aria-hidden="true" />
    <button
      v-for="option in options"
      :key="option"
      type="button"
      role="tab"
      :tabindex="getIsActive(option) ? 0 : -1"
      :aria-selected="getIsActive(option)"
      :class="{isActive: getIsActive(option)}"
      @click="model = option">
      <span class="toggleText">{{ option }}</span>
    </button>
  </div>
</template>

<script setup>
import {nextTick, onMounted, ref, watch} from "vue";
import {useResizeObserver} from "@vueuse/core";

const props = defineProps({
  options: {type: Array, required: true},
  label: {type: String, default: "Segmented control"} // its accessible name
});
const model = defineModel({type: String, default: ""});
const toggleRef = ref(null);
const fillStyle = ref({opacity: 0});
// Space and Enter switch to the next option too: the focus is always on the selected one
const KEY_STEPS = {ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1, " ": 1, Enter: 1};

const getIsActive = (option) => model.value === option;

// an arrow key, Space or Enter: the next option (wrapping), selected and focused
async function onKeydown(event) {
  const step = KEY_STEPS[event.key];
  if (!step) return;
  event.preventDefault();
  const index = props.options.findIndex(getIsActive);
  model.value = props.options[(index + step + props.options.length) % props.options.length];
  await nextTick();
  toggleRef.value?.querySelector('[aria-selected="true"]')?.focus();
}

// the fill under the chosen option: its place and width, measured (they vary with the labels)
function placeFill() {
  const chosen = toggleRef.value?.querySelector('[aria-selected="true"]');
  if (!chosen) return (fillStyle.value = {opacity: 0});
  const {offsetLeft: left, offsetWidth: width} = chosen;
  fillStyle.value = {width: `${width}px`, transform: `translateX(${left}px)`};
}

watch(model, () => nextTick(placeFill));
useResizeObserver(toggleRef, placeFill);
onMounted(() => {
  if (!model.value && props.options.length) model.value = props.options[0];
  nextTick(placeFill);
});
</script>

<style scoped>
/* iOS's segmented control (Seán's spec, 2026-10-23): a fully rounded track with a neutral
   border, and in it a capsule, inset 1px, sliding (180ms) under the chosen option, in the brand's
   light green fill and dark green border; the other option is text on the track */
.toggle {
  position: relative;
  display: flex;
  padding: 1px;
  border: 1px solid #b8b8b8;
  border-radius: 999px;
  background: transparent;
  transition: border-color 0.18s ease;
}
button {
  all: unset;
  position: relative;
  z-index: 1; /* over the fill */
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 1 1 0; /* equal widths */
  min-width: 4.5rem; /* a short label whole (not "Ima…") */
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  font: inherit;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color, #252525);
  transition: color 0.18s ease;
}
.toggleText {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.isActive {
  font-weight: 600;
}
.fill {
  position: absolute;
  top: 1px;
  bottom: 1px;
  left: 0;
  box-sizing: border-box; /* its border within the measured width */
  border: 1.5px solid var(--brand-dark-color);
  border-radius: 999px;
  background: color-mix(in srgb, var(--brand-hover-color) 16%, transparent);
  transition: transform 0.18s ease, width 0.18s ease;
}
/* hover: only the other option, which a click would choose: the track's border and its text */
.toggle:has(button:not(.isActive):hover) {
  border-color: #8c8c8c;
}
button:not(.isActive):hover {
  color: var(--text-color, #000);
  filter: brightness(0.8);
}
@media (prefers-reduced-motion: reduce) {
  .fill,
  button {
    transition: none;
  }
}
/* keyboard focus: one ring round the whole control, outside its rounded frame */
button:focus-visible {
  outline: none !important;
}
.toggle:has(button:focus-visible) {
  outline: 2px solid var(--brand-dark-color, var(--brand-color));
  outline-offset: 2px;
}
</style>
